import type {
  ConversationReadResult,
  ConversationSubscriptionResult,
  ConversationSubscriptionBulkResult,
  ConversationSummary,
  Message,
  RelatedEntity,
  User,
  ConversationParticipant,
  ConversationParticipantCandidate,
  SetParticipantResult
} from "../types"

// The server caps bulk read and subscription writes at 100 items per request (ConversationsController:
// MaxReadItemsPerRequest and the subscription/bulk cap). Chunk to match so a large grid select-all does
// not 400 the whole batch. (Summaries use their own, larger cap inside the summary batcher.)
const MAX_BULK_ITEMS_PER_CALL = 100

export class MessageApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public originalError?: unknown
  ) {
    super(message)
    this.name = 'MessageApiError'
  }
}

export interface ApiConfig {
  apiEndpoint: string
  userToken?: string | (() => string)
}

export interface SendMessageData {
  content: string
  contentType: 'plain' | 'html'
  entityTypeId: string
  entityId: string
  isVisibleToClient: boolean
  isImportant?: boolean
  parentId?: string
}

export interface FetchMessagesParams {
  entities: Array<{ entityTypeId: string; entityId: string }>
}

export interface FetchRelatedEntitiesParams {
  entityTypeId: string
  entityId: string
}

export interface FetchMessageCountParams {
  entityTypeId: string
  entityId: string
}

export interface FetchConversationSummariesParams {
  entityTypeId: string
  entityIds: string[]
}

export interface MarkConversationReadParams {
  entityTypeId: string
  /**
   * `true` marks read, `false` marks unread. Defaults to `true` server-side, so omitting it keeps
   * the behaviour callers had before mark-unread existed.
   */
  read?: boolean
  /** Each item needs a conversationId or an entityId. */
  items: Array<{
    conversationId?: string
    entityId?: string
    /**
     * The message the reader acted on, for a per-message toggle. Without it the whole conversation
     * is marked. Marking read covers this message; marking unread stops just before it.
     */
    atMessageId?: string
  }>
}

export interface ConversationParticipantsParams {
  entityTypeId: string
  conversationId?: string
  entityId?: string
}

export interface SetParticipantParams extends ConversationParticipantsParams {
  targetProfileId: number
  subscribed: boolean
}

export class MessageApiService {
  private config: ApiConfig

  constructor(config: ApiConfig) {
    this.config = config
  }

  /**
   * Get the current user token value by calling the token function
   */
  private getCurrentUserToken(): string {
    if (!this.config.userToken) {
      throw new MessageApiError("UserToken not configured in API service")
    }

    return typeof this.config.userToken === 'function' ? this.config.userToken() : this.config.userToken
  }

  // Convenience methods that use the configured userToken
  async fetchUserWithToken(): Promise<User> {
    return this.fetchUser()
  }

  async fetchRelatedEntitiesWithToken(entityTypeId: string, entityId: string): Promise<RelatedEntity[]> {
    return this.fetchRelatedEntities({ entityTypeId, entityId })
  }

  async fetchMessagesWithToken(entities: Array<{ entityTypeId: string; entityId: string }>): Promise<Message[]> {
    return this.fetchMessages({ entities })
  }

  async fetchMessageCountWithToken(entityTypeId: string, entityId: string): Promise<number> {
    return this.fetchMessageCount({ entityTypeId, entityId })
  }

  async fetchUnreadCountWithToken(entityTypeId: string, entityId: string): Promise<number> {
    return this.fetchUnreadCount({ entityTypeId, entityId })
  }

  async fetchConversationSummariesWithToken(entityTypeId: string, entityIds: string[]): Promise<ConversationSummary[]> {
    return this.fetchConversationSummaries({ entityTypeId, entityIds })
  }

  async markConversationReadWithToken(
    entityTypeId: string,
    entityId: string | string[],
    read = true
  ): Promise<ConversationReadResult> {
    const entityIds = Array.isArray(entityId) ? entityId : [entityId]
    return this.markConversationRead({
      entityTypeId,
      read,
      items: entityIds.map((id) => ({ entityId: id }))
    })
  }

  async sendMessageWithToken(messageData: SendMessageData): Promise<Message> {
    return this.sendMessage(messageData)
  }

  /**
   * Create headers with Authorization Bearer token
   */
  private createHeaders(userToken: string): Record<string, string> {
    if (!userToken) {
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    } else {
      return {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    }
  }

  /**
   * Fetch user data by token
   */
  async fetchUser(): Promise<User> {
    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/user`, {
        headers: this.createHeaders(userToken)
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch user data - status: ${response.status}`,
          response.status
        )
      }

      const userData = await response.json()
      return userData
    } catch (error) {
      console.error("Failed to fetch user data:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch user data", undefined, error)
    }
  }

  /**
   * Fetch related entities for a given entity
   */
  async fetchRelatedEntities(params: FetchRelatedEntitiesParams): Promise<RelatedEntity[]> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams({
        entityTypeId: params.entityTypeId,
        entityId: params.entityId
      })
      const response = await fetch(
        `${this.config.apiEndpoint}/related-entities?${queryParams}`,
        {
          headers: this.createHeaders(userToken)
        }
      )

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch related entities - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      return data.relatedEntities
    } catch (error) {
      console.error("Failed to fetch related entities:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch related entities", undefined, error)
    }
  }

  /**
   * Organize flat messages into threaded structure
   */
  private organizeMessagesIntoThreads(messages: Message[]): Message[] {
    if (!messages?.length) return []
    // Clone and ensure replies array
    const map = new Map<string, Message>()
    for (const m of messages) {
      map.set(m.id, { ...m, replies: Array.isArray(m.replies) ? m.replies : [] })
    }
    const roots: Message[] = []
    const byTime = (a: Message, b: Message) =>
      new Date(a.createdDateTime as any).getTime() - new Date(b.createdDateTime as any).getTime()
    // Stitch parents/children regardless of order
    for (const msg of Array.from(map.values())) {
      const parent = msg.parentId ? map.get(msg.parentId) : undefined
      if (parent) {
        ; (parent.replies ?? (parent.replies = [])).push(msg)
      } else {
        roots.push(msg)
      }
    }
    // Stable sort roots and each replies list by time
    const sortRec = (list: Message[]) => {
      list.sort(byTime)
      for (const child of list) if (child.replies?.length) sortRec(child.replies)
    }
    sortRec(roots)
    return roots
  }

  /**
   * Fetch messages for selected entities
   */
  async fetchMessages(params: FetchMessagesParams): Promise<Message[]> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams(
        params.entities.map((entity) => [
          'entities',
          `${entity.entityTypeId}:${entity.entityId}`
        ])
      )
      const response = await fetch(`${this.config.apiEndpoint}/messages?${queryParams}`, {
        headers: this.createHeaders(userToken)
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch messages - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()

      // Organize flat messages into threaded structure
      const threadedMessages = this.organizeMessagesIntoThreads(data.messages)

      return threadedMessages
    } catch (error) {
      console.error("Failed to fetch messages:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch messages", undefined, error)
    }
  }

  /**
   * Send a new message or reply
   */
  async sendMessage(messageData: SendMessageData): Promise<Message> {
    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/messages`, {
        method: "POST",
        headers: this.createHeaders(userToken),
        body: JSON.stringify(messageData),
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to send message - status: ${response.status}`,
          response.status
        )
      }

      const newMessage = await response.json()
      return newMessage
    } catch (error) {
      console.error("Failed to send message:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to send message", undefined, error)
    }
  }

  /**
   * Fetch message count for an entity
   */
  async fetchMessageCount(params: FetchMessageCountParams): Promise<number> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams({
        entityTypeId: params.entityTypeId,
        entityId: params.entityId
      })
      const response = await fetch(
        `${this.config.apiEndpoint}/messages/message-count?${queryParams}`,
        {
          headers: this.createHeaders(userToken)
        }
      )

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch message count - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      return data.count || 0
    } catch (error) {
      console.error("Failed to fetch message count:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch message count", undefined, error)
    }
  }

  /**
   * Fetch unread message count for an entity
   */
  async fetchUnreadCount(params: FetchMessageCountParams): Promise<number> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams({
        entityTypeId: params.entityTypeId,
        entityId: params.entityId
      })
      const response = await fetch(
        `${this.config.apiEndpoint}/messages/unread-count?${queryParams}`,
        {
          headers: this.createHeaders(userToken)
        }
      )

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch unread count - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      return data.count || 0
    } catch (error) {
      console.error("Failed to fetch unread count:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch unread count", undefined, error)
    }
  }

  /**
   * Badge state for a page of entities, in one call.
   *
   * One entity type per call: the conversation partition key includes the entity type, so a mixed
   * batch would fan out across partitions. Entities with no conversation are absent from the result
   * rather than returned as zeros — treat a missing entry as "no messages".
   */
  async fetchConversationSummaries(
    params: FetchConversationSummariesParams
  ): Promise<ConversationSummary[]> {
    if (params.entityIds.length === 0) return []

    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/conversations/summaries`, {
        method: 'POST',
        headers: this.createHeaders(userToken),
        body: JSON.stringify({
          entityTypeId: params.entityTypeId,
          entityIds: params.entityIds
        })
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch conversation summaries - status: ${response.status}`,
          response.status
        )
      }

      return await response.json()
    } catch (error) {
      console.error("Failed to fetch conversation summaries:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch conversation summaries", undefined, error)
    }
  }

  /**
   * Marks conversations read for the current user.
   *
   * Accepts conversation ids or entity ids, because callers know different things: a grid has ids
   * from a summary, the panel only knows the entity it was opened on.
   *
   * The server writes nothing for a non-participant, so calling this on every open is safe — it does
   * not create a membership document per user per row.
   */
  /**
   * Join or leave a conversation without posting to it.
   *
   * Writes the caller's key into the conversation's granted/revoked buckets, so a grant survives
   * every contextual-access recompute. Internal users only, enforced server-side.
   */
  async setConversationSubscription(params: {
    entityTypeId: string
    entityId?: string
    conversationId?: string
    subscribed: boolean
  }): Promise<ConversationSubscriptionResult> {
    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/conversations/subscription`, {
        method: 'POST',
        headers: this.createHeaders(userToken),
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to change subscription - status: ${response.status}`,
          response.status
        )
      }

      return await response.json()
    } catch (error) {
      console.error("Failed to change conversation subscription:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to change conversation subscription", undefined, error)
    }
  }

  /**
   * Mute or unmute many conversations in one call — the grid's bulk action.
   *
   * <p>A separate route from {@link setConversationSubscription} rather than a widened one: the panel
   * calls the single version, and this package ships on its own release cadence from the service, so
   * changing that request shape would break any deployment where the two versions differ.</p>
   *
   * <p><b>403 means the whole batch was refused and nothing was written.</b> The server will not report
   * per-item access failures, because that would reveal which entity ids exist — so treat a rejection as
   * "none of this happened" and re-read, rather than assuming a partial success.</p>
   */
  async setConversationSubscriptionBulk(params: {
    entityTypeId: string
    subscribed: boolean
    items: Array<{ conversationId?: string; entityId?: string }>
  }): Promise<ConversationSubscriptionBulkResult> {
    const items = params.items ?? []
    const chunks: Array<typeof items> = []
    for (let i = 0; i < items.length; i += MAX_BULK_ITEMS_PER_CALL) {
      chunks.push(items.slice(i, i + MAX_BULK_ITEMS_PER_CALL))
    }
    if (chunks.length === 0) chunks.push([])

    const aggregate: ConversationSubscriptionBulkResult = {
      subscribed: params.subscribed,
      requested: 0,
      changed: 0,
      unchanged: 0,
      noConversation: 0,
      errors: []
    }

    for (const chunk of chunks) {
      try {
        const userToken = this.getCurrentUserToken()
        const response = await fetch(`${this.config.apiEndpoint}/conversations/subscription/bulk`, {
          method: 'POST',
          headers: this.createHeaders(userToken),
          body: JSON.stringify({
            entityTypeId: params.entityTypeId,
            subscribed: params.subscribed,
            items: chunk
          })
        })

        if (!response.ok) {
          throw new MessageApiError(
            `Failed to change subscriptions - status: ${response.status}`,
            response.status
          )
        }

        const result: ConversationSubscriptionBulkResult = await response.json()
        aggregate.requested += result.requested ?? 0
        aggregate.changed += result.changed ?? 0
        aggregate.unchanged += result.unchanged ?? 0
        aggregate.noConversation += result.noConversation ?? 0
        if (result.errors?.length) aggregate.errors.push(...result.errors)
      } catch (error) {
        // Do NOT re-throw: chunking split one logical bulk call into several HTTP requests, and an
        // earlier chunk may already have landed on the server. Throwing here would discard the aggregate
        // that records it, leaving the caller unable to tell that part of the batch succeeded. Record the
        // failure and carry on — the caller distinguishes partial/total failure via `errors.length`.
        console.error("Failed to change conversation subscriptions:", error)
        aggregate.errors.push(error instanceof Error ? error.message : String(error))
      }
    }

    return aggregate
  }

  async fetchParticipantsWithToken(params: ConversationParticipantsParams): Promise<ConversationParticipant[]> {
    return this.fetchParticipants(params)
  }

  async fetchParticipants(params: ConversationParticipantsParams): Promise<ConversationParticipant[]> {
    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/conversations/participants`, {
        method: 'POST',
        headers: this.createHeaders(userToken),
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch participants - status: ${response.status}`,
          response.status
        )
      }

      return await response.json()
    } catch (error) {
      console.error("Failed to fetch participants:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch participants", undefined, error)
    }
  }

  /**
   * People who could be added. `term` searches internal users only; organizational users who can be
   * re-added come back regardless of the term, because they are read off the conversation rather than
   * searched.
   */
  async searchParticipantCandidates(
    params: ConversationParticipantsParams,
    term: string
  ): Promise<ConversationParticipantCandidate[]> {
    try {
      const userToken = this.getCurrentUserToken()
      const query = new URLSearchParams({ term })
      const response = await fetch(
        `${this.config.apiEndpoint}/conversations/participants/candidates?${query}`,
        {
          method: 'POST',
          headers: this.createHeaders(userToken),
          body: JSON.stringify(params)
        }
      )

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to search for people to add - status: ${response.status}`,
          response.status
        )
      }

      return await response.json()
    } catch (error) {
      console.error("Failed to search participant candidates:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to search for people to add", undefined, error)
    }
  }

  async setParticipant(params: SetParticipantParams): Promise<SetParticipantResult> {
    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/conversations/participants`, {
        method: 'PUT',
        headers: this.createHeaders(userToken),
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to change participant - status: ${response.status}`,
          response.status
        )
      }

      return await response.json()
    } catch (error) {
      console.error("Failed to change participant:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to change participant", undefined, error)
    }
  }

  async markConversationRead(
    params: MarkConversationReadParams
  ): Promise<ConversationReadResult> {
    const items = params.items ?? []
    const chunks: Array<typeof items> = []
    for (let i = 0; i < items.length; i += MAX_BULK_ITEMS_PER_CALL) {
      chunks.push(items.slice(i, i + MAX_BULK_ITEMS_PER_CALL))
    }
    if (chunks.length === 0) chunks.push([])

    const aggregate: ConversationReadResult = {
      states: [],
      requested: 0,
      markedRead: 0,
      markedUnread: 0,
      alreadyRead: 0,
      alreadyUnread: 0,
      notSubscribed: 0,
      noConversation: 0,
      notFound: 0,
      errors: []
    }

    for (const chunk of chunks) {
      try {
        const userToken = this.getCurrentUserToken()
        const response = await fetch(`${this.config.apiEndpoint}/conversations/read`, {
          method: 'POST',
          headers: this.createHeaders(userToken),
          body: JSON.stringify({
            entityTypeId: params.entityTypeId,
            read: params.read ?? true,
            items: chunk
          })
        })

        if (!response.ok) {
          throw new MessageApiError(
            `Failed to mark conversation read - status: ${response.status}`,
            response.status
          )
        }

        const result: ConversationReadResult = await response.json()
        if (result.states?.length) aggregate.states!.push(...result.states)
        aggregate.requested += result.requested ?? 0
        aggregate.markedRead += result.markedRead ?? 0
        aggregate.markedUnread += result.markedUnread ?? 0
        aggregate.alreadyRead += result.alreadyRead ?? 0
        aggregate.alreadyUnread += result.alreadyUnread ?? 0
        aggregate.notSubscribed += result.notSubscribed ?? 0
        aggregate.noConversation += result.noConversation ?? 0
        aggregate.notFound += result.notFound ?? 0
        if (result.errors?.length) aggregate.errors.push(...result.errors)
      } catch (error) {
        // See setConversationSubscriptionBulk: with chunking, re-throwing would throw away an aggregate
        // that already records chunks which landed. Record the failure and keep the partial result.
        console.error("Failed to mark conversation read:", error)
        aggregate.errors.push(error instanceof Error ? error.message : String(error))
      }
    }

    return aggregate
  }
}

// Factory function to create API service with default endpoints
export function createMessageApiService(config: Partial<ApiConfig> = {}): MessageApiService {
  const defaultConfig: ApiConfig = {
    apiEndpoint: '/api/messages',
    ...config
  }

  return new MessageApiService(defaultConfig)
}
